import { apiRequest } from './client';

/**
 * `/api/me` — state that belongs to the signed-in user (plan 2.4).
 *
 * The server stores BARE REFERENCES and nothing else: a row is a target type and an id.
 * Labels, colours, icons and visibility are all resolved client-side from the map state the
 * app already holds, which is what makes the rows locale-correct and permission-filtered for
 * free — a reference the current payload cannot resolve is simply not rendered.
 */

export type FavTargetType = 'node' | 'doc';

export interface FavoriteRef {
  targetType: FavTargetType;
  targetId: string;
  /** ISO timestamp; the stable "order starred" the pane lists favorites in. */
  createdAt?: string;
}

export interface RecentRef {
  targetType: 'doc';
  targetId: string;
  /** ISO timestamp of the latest open; the list is newest-first. */
  lastAt: string;
}

export interface MyItemsPayload {
  favorites: FavoriteRef[];
  recents: RecentRef[];
}

export const fetchMyItems = (): Promise<MyItemsPayload> => apiRequest<MyItemsPayload>('/api/me/items');

export const putFavorite = (targetType: FavTargetType, targetId: string): Promise<unknown> =>
  apiRequest('/api/me/favorites', { method: 'PUT', body: { targetType, targetId } });

export const deleteFavorite = (targetType: FavTargetType, targetId: string): Promise<unknown> =>
  apiRequest(`/api/me/favorites/${targetType}/${encodeURIComponent(targetId)}`, { method: 'DELETE' });

export const postRecent = (docId: string): Promise<unknown> =>
  apiRequest('/api/me/recents', { method: 'POST', body: { targetType: 'doc', targetId: docId } });

/* ------------------------------------------- the bell: subscriptions + feed (Phase 3) */

export interface SubscriptionRef {
  targetType: FavTargetType;
  targetId: string;
  createdAt?: string;
}

/** One line in the what-changed feed: the latest revision of one document. */
export interface FeedChange {
  revisionId: string;
  docPageId: string;
  nodeId: string | null;
  /** ISO timestamp of the change. */
  createdAt: string;
  /** Requested locale -> default locale -> the revision's own snapshot title. */
  title: string;
  nodeLabel: string | null;
  actorLabel: string;
  /** Was this a person or the AI — 'human' folds edit + restore, 'ai' folds generate + translate. */
  kind: 'human' | 'ai';
}

/**
 * One notification row — bold until `readAt` is set. FOUR KINDS since V0.41:
 * `revision` is the original "something you watch changed"; `assign_node` / `assign_doc`
 * say "you were made responsible"; `doc_deleted` says a page you held or watched was deleted.
 * Each kind ships the refs it renders from — a revision row has `revisionId`+`docPageId`, an
 * assign_doc row `docPageId`+`locale`, an assign_node row only `nodeId`, a doc_deleted row a
 * `label` (the title, stored because the page is GONE and cannot be looked up) — so every other
 * field is nullable. The human/AI flavour of a revision row moved from `kind` to `changeKind`
 * (null on the others); `actorLabel` on an assignment or deletion row is WHO DID IT, resolved
 * at read time (an erased actor stops resolving).
 */
export interface FeedNotification {
  id: string;
  /** `slug_stale` (V0.45.1) carries the STALE SLUG in `label` — the one thing that cannot be
   *  looked up afterwards, the same reason `doc_deleted` carries its title there. */
  kind:
    | 'revision'
    | 'assign_node'
    | 'assign_doc'
    | 'doc_deleted'
    | 'slug_stale'
    /** V0.50: a to-do handed to you, and one you handed out being finished. */
    | 'todo_assigned'
    | 'todo_done';
  revisionId: string | null;
  docPageId: string | null;
  nodeId: string | null;
  locale: string | null;
  createdAt: string;
  readAt: string | null;
  title: string | null;
  /** The stored title of a since-deleted page (doc_deleted rows only); null otherwise. */
  label: string | null;
  nodeLabel: string | null;
  actorLabel: string | null;
  changeKind: 'human' | 'ai' | null;
}

export interface FeedPayload {
  /** The All-changes watermark — what `unread.changes` counts against. */
  seenAt: string;
  unread: { subscribed: number; changes: number };
  notifications: FeedNotification[];
  changes: FeedChange[];
  page: number;
  hasMore: boolean;
}

export const fetchFeed = (locale: string, page = 0): Promise<FeedPayload> =>
  apiRequest<FeedPayload>('/api/me/feed', { query: { locale, page } });

/**
 * Move the watermark. 'changes' — opening the panel: the All-changes count clears, but a
 * notification stays unread (and bold) until clicked. 'all' — the "Mark all read" button.
 */
export const postFeedSeen = (scope: 'changes' | 'all'): Promise<unknown> =>
  apiRequest('/api/me/feed/seen', { method: 'POST', body: { scope } });

export const postNotificationRead = (id: string): Promise<unknown> =>
  apiRequest(`/api/me/notifications/${encodeURIComponent(id)}/read`, { method: 'POST', body: {} });

/* --------------------------------------------------------- my assignments (V0.38) */

/** One node assigned to me. `via` is the group id when it reaches me through my team. */
export interface AssignedNodeRef {
  nodeId: string;
  via: string | null;
}

/** One doc page+locale assigned to me. Same `via` rule. */
export interface AssignedDocRef {
  docPageId: string;
  locale: string;
  via: string | null;
}

export interface AssignmentsPayload {
  nodes: AssignedNodeRef[];
  docs: AssignedDocRef[];
}

/** Bare refs of everything assigned to me — mine and my team's; direct wins over `via`. */
export const fetchAssignments = (): Promise<AssignmentsPayload> =>
  apiRequest<AssignmentsPayload>('/api/me/assignments');

export const fetchSubscriptions = (): Promise<{ subscriptions: SubscriptionRef[] }> =>
  apiRequest('/api/me/subscriptions');

export const putSubscription = (targetType: FavTargetType, targetId: string): Promise<unknown> =>
  apiRequest('/api/me/subscriptions', { method: 'PUT', body: { targetType, targetId } });

export const deleteSubscription = (targetType: FavTargetType, targetId: string): Promise<unknown> =>
  apiRequest(`/api/me/subscriptions/${targetType}/${encodeURIComponent(targetId)}`, { method: 'DELETE' });
